import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, Input, TextField } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useSettingsStore } from "@user/store/settings_store";

import "./popup_sku_info.scoped.scss";

interface State {
}

const defState: State = {
};

interface SearchParams {
  skuNo: string
}

const defSearchParams: SearchParams = {
  skuNo: ""
};

interface Result {
  info: any,
  skuStockInfos: any[]
}

const defResult = {
  info: {},
  skuStockInfos: []
};

function PopupSkuInfo(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const gridStateKey = "/popup/popup_sku_info";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "관리구분", field: "mngType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("mngType") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 110, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
  });

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    searchParams.skuNo = props.data.skuNo;
    updateSearchParams(searchParams);
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/popup/popup_sku_info.data?mode=search", reqSearchParams, {});
      const info = data.info ?? {};
      let cellStatusName = "";
      if (info.disableCellYn === "Y") cellStatusName = "금지CELL";
      else if (info.workStatusEquip === "ERROR") cellStatusName = "오류CELL";
      else if (info.inoutType === "I") cellStatusName = "입고작업";
      else if (info.inoutType === "O") cellStatusName = "출고작업";
      else if (info.workType === "A") cellStatusName = "실사작업";
      else if (Number(info.stockQty) > 0) cellStatusName = "적재SKU";
      else if (Number(info.stockQty) == 0) cellStatusName = "공SKU";
      info.cellStatusName = cellStatusName;
      setResult({
        info: info,
        skuStockInfos: data.skuStockInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "800px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          SKU 상세 정보
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* SKU 정보 */}
            <div className="table-wrap md">
              <table>
                <colgroup>
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">SKU No</th>
                    <td colSpan={3}>
                      { result.info.skuNoEx }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">상태</th>
                    <td>
                      { result.info.cellStatusName }
                    </td>
                    <th scope="row">SKU Type</th>
                    <td>
                      { commonCode.getCodeValue("skuType", result.info.skuType) }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">SKU Size</th>
                    <td>
                      { commonCode.getCodeValue("skuSize", result.info.skuSize) }
                    </td>
                    <th scope="row">SKU Cell</th>
                    <td>
                      { valueFormatter.number(result.info.skuCellQty) }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// SKU 정보 */}
            {/* 그리드 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={result.skuStockInfos}
                  defaultColDef={kutil.defaultColDef}
                  initialState={gridState}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                />
              </div>
            </AgGridProvider>
            {/*// 그리드 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupSkuInfo);
